<template>
    <div>
        <general-order-details ref="generalOrderDetails"
                               :return-url="returnUrl"
                               :create-order-url="createOrderUrl"
                               :edit-order-url-template="editOrderUrlTemplate"
                               :api-base-url="apiBaseUrl"
                               :file-download-url="fileDownloadUrl"
                               :current-user-account-id="currentUserAccountId"
                               :order="order"
                               :selectable-clients="currentSelectableClients"
                               :initial-client-manages-assigned-contracts="initialClientManagesAssignedContracts"
                               :initial-client-branches="initialClientBranches"
                               :initial-ordering-client-contact="initialOrderingClientContact"
                               :initial-billing-contact="initialBillingContact"
                               :prompt-for-billing-contact="(order.clientOrderRequest == null && order.quote == null)"
                               :selectable-service-types="currentSelectableServiceTypes"
                               service-types-null-select-placeholder="Select a court/tribunal/other first"
                               :selectable-service-delays="currentSelectableServiceDelays"
                               :selectable-non-production-charge-types="currentSelectableNonProductionChargeTypes"
                               :possible-non-production-dates="allPossibleNonProductionDates"
                               :non-production-charges="nonProductionCharges"
                               :attachments="attachments"
                               :all-attachment-classifications="allAttachmentClassifications"
                               :all-transcript-delivery-options="allTranscriptDeliveryOptions"
                               :all-mode-of-sitting-options="allModeOfSittingOptions"
                               :default-delivery-contacts="currentDefaultDeliveryContacts"
                               :initial-delivery-contacts="initialDeliveryContacts"
                               :initial-delivery-cc-contacts="initialDeliveryCcContacts"
                               :initial-delivery-bcc-contacts="initialDeliveryBccContacts"
                               :delivery-contact-ids="deliveryContactIds"
                               :delivery-contact-cc-ids="deliveryContactCcIds"
                               :delivery-contact-bcc-ids="deliveryContactBccIds"
                               :emails-link="emailsLink"
                               :all-audio-sub-status-options="allAudioSubStatusOptions"
                               :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                               :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                               :all-deposit-sub-status-options="allDepositSubStatusOptions"
                               :all-vetting-sub-status-options="allVettingSubStatusOptions"
                               :can-edit-order="canEditOrder"
                               :can-view-attachments="canViewAttachments"
                               :can-create-comment="canCreateComment"
                               :can-create-billing-information-comment="canCreateBillingInformationComment"
                               :can-edit-and-retract-all-comments="canEditAndRetractAllComments"
                               :can-view-sub-status-details="canViewSubStatusDetails"
                               :can-modify-audio-sub-status="canModifyAudioSubStatus"
                               :can-modify-log-notes-sub-status="canModifyLogNotesSubStatus"
                               :can-modify-release-approval-sub-status="canModifyReleaseApprovalSubStatus"
                               :can-modify-deposit-sub-status="canModifyDepositSubStatus"
                               :can-modify-vetting-sub-status="canModifyVettingSubStatus"
                               :hide-distribution-list="true"
                               :hide-default-distribution-list="hideDistributionList"
                               :hide-service-type-and-delay-selection="true"
                               :readonly="readonly"
                               :validation-observer="validationObserver"
                               :save="saveOrder"
                               :validate="validate"
                               v-on:ordering-client-change="orderingClientChanged"
                               v-on:service-type-change="serviceTypeChanged"
                               :initial-transcript-delivery-options="initialTranscriptDeliveryOptions"
                               :currency-code="currencyCode">
            <b-form-row>
                <validation-provider rules="required" name="Court/Tribunal/Other" v-slot="vp" slim>
                    <b-form-group label="Court/Tribunal/Other"
                                  label-for="courtRegistryId"
                                  class="col-md-4">
                        <select2 name="courtRegistryId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isUuidNullOrEmpty(orderingClientId) || isICMS"
                                 :options="initialCourtRegistries"
                                 enable-ajax
                                 :placeholder="isUuidNullOrEmpty(orderingClientId) ? 'Select a client first' : 'Select a court/tribunal/other'"
                                 key-property="courtRegistryId"
                                 text-property="name"
                                 v-model="courtRegistryId"
                                 v-on:search="findCourtRegistries"                                 
                                 v-on:select="courtRegistrySelected"
                                 :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group class="col-md-4 align-self-end">
                    <b-form-checkbox name="isExempt"
                                     v-model="isExempt"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrder">
                        Fee Exempt
                    </b-form-checkbox>
                </b-form-group>
                <validation-provider ref="clientParticipationInMatterSelectValidator" rules="required" :name="$t('Entities.Shared.ClientParticipationInMatter')" v-slot="vp" slim>
                    <b-form-group :label="$t('Entities.Shared.ClientParticipationInMatter')"
                                  label-for="clientParticipationInMatter"
                                  class="col-md-4">
                        <select2 name="clientParticipationInMatter"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isICMS"
                                 v-model="clientParticipationInMatter"
                                 :options="allCourtOrderClientParticipationInMatters"
                                 disable-search
                                 placeholder="Select how the client participates"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>

            <court-matter-select name="courtMatterId"
                                 :mode="(order.clientOrderRequest || order.quote) ? 'match' : 'select'"
                                 :client-order-request-id="order.clientOrderRequest ? order.clientOrderRequest.clientOrderRequestId : null"
                                 :order-id="order.orderId"
                                 :quote-id="order.quote ? order.quote.quoteId : null"
                                 :court-registry-id="courtRegistryId"
                                 :requestedCourtJurisdictionId="requestedCourtJurisdictionId"
                                 :requestedCourtJurisdictionName="requestedCourtJurisdictionName"
                                 :requestedMatterNumber="requestedMatterNumber"
                                 :requestedMatterName="requestedMatterName"
                                 :requestedCourtRoomOther="requestedCourtRoomOther"
                                 :requestedCourtJudgeIds="requestedCourtJudgeIds"
                                 :requestedCourtJudgeNames="requestedCourtJudgeNames"
                                 :requestedCourtSittings="requestedCourtSittings"
                                 v-model="courtMatterId"
                                 :initial-court-matters="initialCourtMatters"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || !courtRegistryId || isICMS"
                                 :disabledEditMatterDetails="readonly || !canEditOrder || isICMS"
                                 v-on:court-matter-change="courtMatterChange">
            </court-matter-select>


            <court-appearances :api-controller-url="apiBaseUrl + '/courtappearance'"
                               :court-matter-id="courtMatterId"
                               :disabled="readonly || !canEditOrder"
                               class="mb-2"
                               v-if="courtMatterId != null">
            </court-appearances>

            <court-sittings :api-base-url="apiBaseUrl"
                            :court-registry-id="courtRegistryId"
                            :court-matter-id="courtMatterId"
                            :client-id="orderingClientId"
                            :order-id="order.orderId"
                            :selectable-service-types="currentSelectableServiceTypes"
                            :selectable-service-delays="currentSelectableServiceDelays"
                            :disabled="readonly || !canEditOrder || isICMS"
                            :requestedCourtSittings="requestedCourtSittings"
                            v-model="courtSittingOrderAssignments"
                            :all-court-sitting-matter-types="allCourtSittingMatterTypes"
                            :is-confirmed-order="order.confirmedDateTimeUTC != null"
                            :order-service-type-requires-service-delay="serviceTypeRequiresServiceDelay"
                            :all-mode-of-sitting-options="allModeOfSittingOptions"
                            :all-audio-quality-options="allAudioQualityOptions"
                            :hide-distribution-list="hideDistributionList"
                            :initial-service-type-id="order.quote?.serviceTypeId != null ? order.quote.serviceTypeId : null"
                            :initial-service-delay-id="order.quote?.serviceDelayId != null ? order.quote.serviceDelayId : null"
                            class="mb-2"
                            v-if="courtMatterId != null">
            </court-sittings>
            <b-form-row>
                <b-form-group :label="$t('Entities.CourtOrder.AdditionalCourtMatters')"
                              label-for="additionalCourtMatters"
                              class="col">
                    <b-form-textarea name="additionalCourtMatters"
                                     v-model="additionalCourtMatters"
                                     :disabled="readonly || !canEditOrder">
                    </b-form-textarea>
                </b-form-group>
            </b-form-row>
        </general-order-details>
    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import { DatePickerPlugin, DateTimePickerPlugin } from '@syncfusion/ej2-vue-calendars';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);

    import Select2 from '../Select2';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';

    import GeneralOrderDetails from './GeneralOrderDetails.vue'
   
    Vue.use(BootstrapVue);
    Vue.use(DatePickerPlugin);
    Vue.use(DateTimePickerPlugin);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('general-order-details', GeneralOrderDetails);
   

    export default {
        name: 'CourtOrderDetails',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        props: {
            returnUrl: {
                type: String,
                required: true
            },
            createOrderUrl: {
                type: String,
                required: true
            },

            editOrderUrlTemplate: {
                type: String,
                required: true
            }, 
            apiBaseUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            order: {
                type: Object,
                required: true
            },
            selectableClients: {
                type: Array,
                default: null
            },
            initialClientManagesAssignedContracts: {
                type: Boolean,
                default: false
            },
            initialClientBranches: {
                type: Array,
                default: null
            },
            initialOrderingClientContact: {
                type: Object,
                default: null
            },
            initialBillingContact: {
                type: Object,
                default: null
            },
            initialCourtRegistry: {
                type: Object,
                default: null
            },
            initialServiceTypes: {
                type: Array,
                default: null
            },
            initialServiceDelays: {
                type: Array,
                default: null
            },
            initialNonProductionChargeTypes: {
                type: Array,
                default: null
            },
            initialPossibleNonProductionDates: {
                type: Array,
                default: null
            },
            initialCourtSittingOrderAssignments: {
                type: Array,
                default: null
            },
            initialCourtMatters: {
                type: Array,
                default: null
            },
            nonProductionCharges: {
                type: Array,
                default: null
            },
            attachments: {
                type: Array,
                default: null
            },
            allCourtOrderClientParticipationInMatters: {
                type: Array,
                default: null
            },        
            initialCourtSittingMatterTypes: {
                type: Array,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                default: null
            },
            allTranscriptDeliveryOptions: {
                type: Array,
                default: null
            },
            allModeOfSittingOptions: {
                type: Array,
                default: null
            },
            allAudioQualityOptions: {
                type: Array,
                default: null
            },
            defaultDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryCcContacts: {
                type: Array,
                default: null
            },
            initialDeliveryBccContacts: {
                type: Array,
                default: null
            },
            deliveryContactIds: {
                type: Array,
                default: null
            },
            deliveryContactCcIds: {
                type: Array,
                default: null
            },
            deliveryContactBccIds: {
                type: Array,
                default: null
            },
            emailsLink: {
                type: String,
                default: null
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            canEditOrder: {
                type: Boolean,
                requird: true
            },
            canViewAttachments: {
                type: Boolean,
                requird: true
            },
            canCreateComment: {
                type: Boolean,
                default: false
            },
            canCreateBillingInformationComment: {
                type: Boolean,
                default: false
            },
            canEditAndRetractAllComments: {
                type: Boolean,
                default: false
            },
            canViewSubStatusDetails: {
                type: Boolean,
                default: false
            },
            canModifyAudioSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyLogNotesSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyReleaseApprovalSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyDepositSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyVettingSubStatus: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false
            },
            currencyCode: {
                type: String,
                required: true
            },
        },
        data: function () {
            return {
                orderingClientId: this.isUuidNullOrEmpty(this.order.orderingClientId) ? null : this.order.orderingClientId,
                orderingClientBranchId: this.isUuidNullOrEmpty(this.order.orderingClientBranchId) ? null : this.order.orderingClientBranchId,
                clientParticipationInMatter: this.order.clientParticipationInMatter ? this.order.clientParticipationInMatterString : null,
                initialCourtRegistries: (this.initialCourtRegistry == null) ? null : [this.initialCourtRegistry],
                // josef
                courtRegistryId: this.isUuidNullOrEmpty(this.order.courtRegistryId) ? '' : this.order.courtRegistryId,

                initialTranscriptDeliveryOptions: null,               

                courtMatterId: this.isUuidNullOrEmpty(this.order.courtMatterId) ? null : this.order.courtMatterId,
                allCourtSittingMatterTypes: this.initialCourtSittingMatterTypes,
                courtSittingOrderAssignments: this.initialCourtSittingOrderAssignments,
                additionalCourtMatters: this.order.additionalCourtMatters,
                currentSelectableServiceTypes: this.initialServiceTypes,
                serviceTypeRequiresServiceDelay: (this.order.serviceType == null) ? false : this.order.serviceType.requiresServiceDelay,
                currentSelectableServiceDelays: this.initialServiceDelays,
                currentSelectableNonProductionChargeTypes: this.initialNonProductionChargeTypes,
                allPossibleNonProductionDates: this.initialPossibleNonProductionDates,
                currentDefaultDeliveryContacts: this.defaultDeliveryContacts,
                isExempt: this.order.isExempt,
                courtRoomId: (this.order.court != null) ? this.order.listCourt.courtRoomId : null,
                sittingDate: (this.order.court != null) ? this.parseDateTime(this.order.listCourt.sittingDate, null, false, false) : null,                
                startTime: (this.order.court != null) ? this.formatDateTime(this.order.court.startTime, 'HH:mm', true, true) : null,
                endTime: (this.order.court != null) ? this.formatDateTime(this.order.court.endTime, 'HH:mm', true, true) : null,
                description: (this.order.court != null) ? this.order.court.description : null,
                showOrderMatchModal: false,
                tempToggleVar: false,
                hideDistributionList: (this.order.courtRegistry == null) ? false : this.order.courtRegistry.hideDistributionList,
                currentSelectableClients: this.selectableClients,
            };
        },
        computed: {
            validationObserver: function () {
                return this.$refs.validationObserver;
            },

            requestedCourtJudgeIds: function () {
                if (this.order.clientOrderRequest == null ||
                    this.order.clientOrderRequest.clientCourtOrderRequestJudgeAssignments == null ||
                    this.order.clientOrderRequest.clientCourtOrderRequestJudgeAssignments.length == 0) {
                    return null;
                }
                var courtJudgeIds = []
                this.order.clientOrderRequest.clientCourtOrderRequestJudgeAssignments.forEach((element) => {
                    courtJudgeIds.push(element.courtJudge.courtJudgeId);
                });
                return courtJudgeIds;
            },
            requestedCourtJudgeNames: function () {
                if (this.order.clientOrderRequest == null ||
                    this.order.clientOrderRequest.clientCourtOrderRequestJudgeAssignments == null ||
                    this.order.clientOrderRequest.clientCourtOrderRequestJudgeAssignments.length == 0) {
                    return null;
                }
                var courtJudgeNames = []
                this.order.clientOrderRequest.clientCourtOrderRequestJudgeAssignments.forEach((element) => {
                    courtJudgeNames.push(element.courtJudge.displayName);
                });
                return courtJudgeNames;
            },

            requestedDeliveryContactIds: function () {
                if (this.order.clientOrderRequest == null ||
                    this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments == null ||
                    this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments.length == 0) {
                    return null;
                }
                var deliveryContactIds = []
                this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments.forEach((element) => {
                    deliveryContactIds.push(element.clientContact.contactId);
                });
                return deliveryContactIds;
            },
            requestedDeliveryContactGrouped: function () {
                if (this.order.clientOrderRequest == null ||
                    this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments == null ||
                    this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments.length == 0) {
                    return null;
                }
                var deliveryContacts = {
                    ids: {
                        To: [],
                        Cc: [],
                        Bcc: []
                    },
                    names: {
                        To: [],
                        Cc: [],
                        Bcc: []
                    }
                };
                this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments.forEach((element) => {
                    if (element.deliveryContactType == 0) {
                        deliveryContacts.ids.To.push(element.clientContact.contactId);
                        deliveryContacts.names.To.push(element.clientContact.name);
                    }
                    if (element.deliveryContactType == 1) {
                        deliveryContacts.ids.Cc.push(element.clientContact.contactId);
                        deliveryContacts.names.Cc.push(element.clientContact.name);
                    }
                    if (element.deliveryContactType == 2) {
                        deliveryContacts.ids.Bcc.push(element.clientContact.contactId);
                        deliveryContacts.names.Bcc.push(element.clientContact.name);
                    }
                });
                return deliveryContacts;
            },
            requestedDeliveryContactNames: function () {
                if (this.order.clientOrderRequest == null ||
                    this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments == null ||
                    this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments.length == 0) {
                    return null;
                }
                var deliveryContactNames = []
                this.order.clientOrderRequest.clientOrderRequestDeliveryContactAssignments.forEach((element) => {
                    deliveryContactNames.push(element.clientContact.name);
                });
                return deliveryContactNames;
            },
            requestedCourtJurisdictionId: function () {
                if (this.order.clientOrderRequest != null && this.order.clientOrderRequest.courtJurisdiction != null)
                    return this.order.clientOrderRequest.courtJurisdiction.courtJurisdictionId;
                else if (this.order.quote != null && this.order.quote.courtJurisdiction != null)
                    return this.order.quote.courtJurisdiction.courtJurisdictionId;
                else
                    return null;
            },
            requestedCourtJurisdictionName: function () {
                if (this.order.clientOrderRequest != null && this.order.clientOrderRequest.courtJurisdiction != null)
                    return this.order.clientOrderRequest.courtJurisdiction.name;
                else if (this.order.quote != null && this.order.quote.courtJurisdiction != null)
                    return this.order.quote.courtJurisdiction.name;
                else
                    return null;
            },
            requestedMatterNumber: function () {
                if (this.order.clientOrderRequest != null)
                    return this.order.clientOrderRequest.matterNumber;
                else if (this.order.quote != null && this.order.quote.matterNumber != null)
                    return this.order.quote.matterNumber;
                else
                    return null;
            },
            requestedMatterName: function () {
                if (this.order.clientOrderRequest != null)
                    return this.order.clientOrderRequest.matterName;
                else if (this.order.quote != null && this.order.quote.matterName != null)
                    return this.order.quote.matterName;
                else
                    return null;
            },
            requestedCourtSittings: function () {
                if (this.order.clientOrderRequest != null) {
                    return [
                        {
                            sittingDate: this.order.clientOrderRequest.courtSittingDate,
                            startTime: this.order.clientOrderRequest.courtSittingStartTime,
                            endTime: this.order.clientOrderRequest.courtSittingEndTime,
                            courtRoomId: this.order.clientOrderRequest.courtRoomId,
                            courtLocationRoomName: this.order.clientOrderRequest.courtRoom ? this.order.clientOrderRequest.courtRoom.courtLocationRoomName : null,
                            courtSittingMatterTypeId: this.order.clientOrderRequest.courtSittingMatterTypeId,
                            assignedJudgeIds: this.requestedCourtJudgeIds,
                            assignedJudgeDisplayNames: this.requestedCourtJudgeNames,
                            deliveryContactIds: this.requestedDeliveryContactGrouped != null ? this.requestedDeliveryContactGrouped.ids.To : null,
                            deliveryContactCcIds: this.requestedDeliveryContactGrouped != null ? this.requestedDeliveryContactGrouped.ids.Cc : null,
                            deliveryContactBccIds: this.requestedDeliveryContactGrouped != null ? this.requestedDeliveryContactGrouped.ids.Bcc : null,
                            deliveryContactNames: this.requestedDeliveryContactNames,
                            serviceTypeId: this.order.clientOrderRequest.serviceTypeId ? this.order.clientOrderRequest.serviceTypeId : null,
                            serviceType: this.order.clientOrderRequest.serviceType ? this.order.clientOrderRequest.serviceType : null,
                            serviceDelayId: this.order.clientOrderRequest.serviceDelayId ? this.order.clientOrderRequest.serviceDelayId : null,
                            serviceDelay: this.order.clientOrderRequest.serviceDelay ? this.order.clientOrderRequest.serviceDelay : null,
                            groupedDeliveryContactNames: this.requestedDeliveryContactGrouped != null ? this.requestedDeliveryContactGrouped.names : null,
                        }
                    ]
                }
                else if (this.order.quote != null && this.order.quote.sittingDays != null && this.order.quote.sittingDays.length > 0) {
                    var courtSittings = [];
                    this.order.quote.sittingDays.forEach(s => {
                        courtSittings.push({
                            sittingDate: s.sittingDate,
                            startTime: s.startTime,
                            endTime: s.endTime,
                            courtRoomId: null,
                            courtLocationRoomName: null,
                            courtSittingMatterTypeId: null,
                        })
                    });
                    return courtSittings;
                }
                return null;
            },
            isICMS: function () {
                return this.order.externalSource != null;
            },
            requestedCourtRoomOther: function () {
                if (this.order.clientOrderRequest != null)
                    return this.order.clientOrderRequest.courtRoomOther;
                else
                    return null;
            },
        },
        watch: {
            courtRegistryId: function () {
                this.additionalCourtMatters = null;
                this.reloadCourtSittingMatterTypes();
                this.reloadServiceTypes();
                this.reloadServiceDelays();
                this.reloadNonProductionChargeTypes();
            },
            courtMatterId: function () {
                if (this.courtMatterId == null) this.courtSittingOrderAssignments = null;
                    this.reloadDefaultDeliveryContacts();
            },
            courtSittingOrderAssignments: function () {
                if (this.courtSittingOrderAssignments) {
                    this.allPossibleNonProductionDates = [];
                    if (this.courtSittingOrderAssignments) {
                        this.courtSittingOrderAssignments.forEach((courtSittingOrderAssignment) => {
                            var text = '';
                            if (courtSittingOrderAssignment.sittingDayCode) text = 'Day ' + courtSittingOrderAssignment.sittingDayCode + ': ';
                            text += this.formatDateTime(courtSittingOrderAssignment.sittingDate, 'dddd DD/MM/YYYY');
                            this.allPossibleNonProductionDates.push({
                                date: courtSittingOrderAssignment.sittingDate,
                                text: text
                            });
                        });
                    }
                } else {
                    this.allPossibleNonProductionDates = null;
                }
            }
        },
        methods: {
            courtMatterChange: function () {
                this.loadTranscriptDeliveryOptions();
            },
            loadTranscriptDeliveryOptions: function () {

                this.currentErrors = [];
                var thisVueApp = this;

                //console.log('loading delivery options.');

                var url = this.apiBaseUrl + '/courtmatter/transcriptdeliveryoptions/' + this.courtMatterId;
                axios
                    .get(url)
                    .then(function (response) {
                        thisVueApp.initialTranscriptDeliveryOptions = response.data;
                        //console.log(JSON.stringify(thisVueApp.initialTranscriptDeliveryOptions));
                    })
                    .catch(function (ex) {
                        thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                    })
                    .finally(function () {
                        if (thisVueApp.currentErrors.length != 0) {
                            thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                        }
                    });
               
            },
            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },
            orderingClientChanged: function (newOrderingClientId) {
                this.orderingClientId = newOrderingClientId;
            },
            courtRegistrySelected: function (courtRegistry) {
                if (courtRegistry != null) {
                    this.hideDistributionList = courtRegistry.hideDistributionList;

                    //if order is on hold
                    if (this.order.status == 0) {
                        this.reloadSelectableClients();
                    }
                }
                else {
                    this.hideDistributionList = false;
                }                
            },
            findCourtRegistries: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiControllerUrl('courtregistry');
                url += '/byclient/' + this.orderingClientId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtRegistryId;
                                obj.text = obj.name;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            reloadSelectableClients: function () {
                var filter = {
                    searchTerm: ''
                };

                if (this.courtRegistryId) {
                    var url = this.$urlHelper.getApiUrl('client', 'bycourtregistry', [this.courtRegistryId, 'find']);

                    //console.log('url: ' + url);
                    axios
                        .post(url, JSON.stringify(filter), {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                        .then(response => {
                            var mappedData = response.data;

                            var selectableList = [];

                            mappedData.forEach((element) => {
                                var item = {
                                    disabled: false,
                                    group: null,
                                    id: element.clientId,
                                    selected: element.clientId == this.orderingClientId,
                                    text: element.name,
                                    value: element.clientId,
                                };

                                selectableList.push(item);
                            });
                            this.currentSelectableClients = selectableList;
                        })
                        .catch(ex => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableClients = this.selectableClients;
                }
            },
            reloadCourtSittingMatterTypes: function () {
                this.currentErrors = [];
                var thisVueApp = this;
                if (this.courtRegistryId) {
                    var url = this.apiBaseUrl + '/courtsittingmattertype/bycourtregistry/' + this.courtRegistryId;
                    axios
                        .get(url)
                        .then(function (response) {
                            thisVueApp.allCourtSittingMatterTypes = response.data;
                        })
                        .catch(function (ex) {
                            thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                        })
                        .finally(function () {
                            if (thisVueApp.currentErrors.length != 0) {
                                thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                            }
                        });
                } else {
                    this.allCourtSittingMatterTypes = null;
                }
            },
            reloadServiceTypes: function () {
                this.currentErrors = [];
                var thisVueApp = this;
                if (this.courtRegistryId) {
                    var url = this.apiBaseUrl + '/servicetype/bycourtregistry/' + this.courtRegistryId;
                    axios
                        .get(url)
                        .then(function (response) {
                            thisVueApp.currentSelectableServiceTypes = response.data;
                        })
                        .catch(function (ex) {
                            thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                        })
                        .finally(function () {
                            if (thisVueApp.currentErrors.length != 0) {
                                thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                            }
                        });
                } else {
                    this.currentSelectableServiceTypes = null;
                }
            },
            reloadServiceDelays: function () {
                this.currentErrors = [];
                var thisVueApp = this;
                if (this.courtRegistryId) {
                    var url = this.apiBaseUrl + '/servicedelay/bycourtregistry/' + this.courtRegistryId;
                    axios
                        .get(url)
                        .then(function (response) {
                            thisVueApp.currentSelectableServiceDelays = response.data;
                        })
                        .catch(function (ex) {
                            thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                        })
                        .finally(function () {
                            if (thisVueApp.currentErrors.length != 0) {
                                thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                            }
                        });
                } else {
                    this.currentSelectableServiceDelays = null;
                }
            },
            reloadNonProductionChargeTypes: function () {
                this.currentErrors = [];
                var thisVueApp = this;
                if (this.courtRegistryId) {
                    var url = this.apiBaseUrl + '/nonproductionchargetype/bycourtregistry/' + this.courtRegistryId + '/rates/true';
                    axios
                        .get(url)
                        .then(function (response) {
                            thisVueApp.currentSelectableNonProductionChargeTypes = response.data;
                        })
                        .catch(function (ex) {
                            thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                        })
                        .finally(function () {
                            if (thisVueApp.currentErrors.length != 0) {
                                thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                            }
                        });
                } else {
                    this.currentSelectableNonProductionChargeTypes = null;
                }
            },
            reloadDefaultDeliveryContacts: function () {
                this.currentDefaultDeliveryContacts = null;
                this.currentErrors = [];
                var thisVueApp = this;
                if (this.courtMatterId) {
                    var url = this.apiBaseUrl + '/contact/jurisdictiondelivery/bycourtmatter/' + this.courtMatterId;
                    axios
                        .get(url)
                        .then(function (response) {
                            thisVueApp.currentDefaultDeliveryContacts = response.data;
                        })
                        .catch(function (ex) {
                            thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                        })
                        .finally(function () {
                            if (thisVueApp.currentErrors.length != 0) {
                                thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                            }
                        });
                } else {
                    this.currentDefaultDeliveryContacts = null;
                }
            },
            serviceTypeChanged: function (serviceType) {
                if (serviceType != null) {
                    this.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                }
                else {
                    this.serviceTypeRequiresServiceDelay = false;
                }
            },
            saveOrder: function (generalData) {
                var data = {
                    ...generalData,
                    courtSittingOrderAssignments: this.courtSittingOrderAssignments
                };
                data.order.quoteClientIdToCopy = !this.isUuidNullOrEmpty(this.order?.quoteClientIdToCopy) ? this.order.quoteClientIdToCopy : null;
                data.order.serviceTypeId = this.courtSittingOrderAssignments[0].serviceTypeId;
                data.order.serviceDelayId = this.courtSittingOrderAssignments[0].serviceDelayId;
                data.order.clientParticipationInMatter = this.clientParticipationInMatter;
                data.order.courtRegistryId = this.courtRegistryId;
                data.order.courtMatterId = this.courtMatterId;
                data.order.additionalCourtMatters = this.additionalCourtMatters;
                data.order.isExempt = this.isExempt;
                var url = this.apiControllerUrl('order');
                url += '/court';
                return axios
                    .request(
                        {
                            method: this.isUuidNullOrEmpty(this.order.orderId) ? 'post' : 'put',
                            url: url,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        });
            },
            validate: function () {
                var validationErrors = [];
                if (this.courtSittingOrderAssignments == null || this.courtSittingOrderAssignments.length == 0) {
                    validationErrors.push("At least one sitting has to be included in the order.");
                } else {
                    var isConfirmedOrder = (this.order.confirmedDateTimeUTC != null);
                    this.courtSittingOrderAssignments.forEach(e => {
                        var validateServiceTypeAndDelay = true;
                        if (isConfirmedOrder && e.courtOrderId) { //this means, the order is already confirmed and the sitting is an existing entry - so we do not validate service type/delay cause that cannot have been modified
                            validateServiceTypeAndDelay = false;
                        }
                        if (validateServiceTypeAndDelay) {
                            if (e.serviceTypeId == null) {
                                validationErrors.push("A service type selection is required for sitting '" + e.sittingDayDateTimeDisplayText + "'.");
                            } else if (e.serviceTypeRequiresServiceDelay && e.serviceDelayId == null) {
                                validationErrors.push("A turnaround selection is required for sitting '" + e.sittingDayDateTimeDisplayText + "'.");
                            }
                        }
                        if ((!this.hideDistributionList && !e.serviceTypeHideDistributionList) && (e.deliveryContactIds == null || e.deliveryContactIds.length == 0)) {
                            validationErrors.push("A distribution list is required for sitting '" + e.sittingDayDateTimeDisplayText + "'.");
                        }
                    });
                }
                if (validationErrors.length > 0) {
                    this.showErrorsToast(validationErrors, 'Please correct the following input errors', false);
                    return false;
                }
                return true;
            }
        },
        mounted: function () {
            if (this.order.status == 0 && this.order.quote == null) {
                this.reloadSelectableClients();
            }
        }
    }
</script>
